/**
 * Utilidades para agrupación cronológica y formato de fechas estilo Freepik.
 * Respeta la zona horaria del navegador del usuario.
 */

const MONTH_NAMES_ES = [
  "ENERO", "FEBRERO", "MARZO", "ABRIL", "MAYO", "JUNIO",
  "JULIO", "AGOSTO", "SEPTIEMBRE", "OCTUBRE", "NOVIEMBRE", "DICIEMBRE"
];

const MONTH_NAMES_EN = [
  "JANUARY", "FEBRUARY", "MARCH", "APRIL", "MAY", "JUNE",
  "JULY", "AUGUST", "SEPTEMBER", "OCTOBER", "NOVEMBER", "DECEMBER"
];

/**
 * Genera el encabezado de fecha en mayúsculas estilo Freepik:
 * - "HOY — 18 DE OCTUBRE DE 2026"
 * - "AYER — 17 DE OCTUBRE DE 2026"
 * - "16 DE OCTUBRE DE 2026"
 */
export function formatDateGroupHeading(date, lang = "es") {
  if (!date || isNaN(new Date(date).getTime())) {
    return lang === "es" ? "SIN FECHA" : "NO DATE";
  }

  const d = new Date(date);
  const now = new Date();

  // Comparar días a medianoche en la zona horaria local
  const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const targetMidnight = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round((todayMidnight - targetMidnight) / (1000 * 60 * 60 * 24));

  const day = d.getDate();
  const year = d.getFullYear();

  if (lang === "es") {
    const month = MONTH_NAMES_ES[d.getMonth()];
    const fullDate = `${day} DE ${month} DE ${year}`;
    if (dayDiff === 0) return `HOY — ${fullDate}`;
    if (dayDiff === 1) return `AYER — ${fullDate}`;
    return fullDate;
  } else {
    const month = MONTH_NAMES_EN[d.getMonth()];
    const fullDate = `${month} ${day}, ${year}`;
    if (dayDiff === 0) return `TODAY — ${fullDate}`;
    if (dayDiff === 1) return `YESTERDAY — ${fullDate}`;
    return fullDate;
  }
}

/**
 * Agrupa una lista de elementos por día en orden cronológico descendente.
 * Dentro de cada día, los elementos se ordenan del más reciente al más antiguo.
 *
 * @param {Array} items Lista de elementos a agrupar.
 * @param {Function} getDateFn Función que extrae la fecha ISO o timestamp de cada elemento.
 * @param {string} lang Idioma ("es" o "en").
 * @returns {Array<{ key: string, title: string, date: Date, items: Array }>}
 */
export function groupItemsByDate(items = [], getDateFn = (item) => item?.createdAt, lang = "es") {
  if (!Array.isArray(items) || items.length === 0) return [];

  const groupsMap = new Map();

  // Asegurar orden inicial por fecha descendente
  const sorted = [...items].sort((a, b) => {
    const da = new Date(getDateFn(a) || 0).getTime();
    const db = new Date(getDateFn(b) || 0).getTime();
    return db - da;
  });

  for (const item of sorted) {
    const rawDate = getDateFn(item);
    let dateObj = rawDate ? new Date(rawDate) : null;
    let key;
    let title;

    if (!dateObj || isNaN(dateObj.getTime())) {
      key = "no_date";
      title = lang === "es" ? "SIN FECHA" : "NO DATE";
      dateObj = new Date(0);
    } else {
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, "0");
      const day = String(dateObj.getDate()).padStart(2, "0");
      key = `${year}-${month}-${day}`;
      title = formatDateGroupHeading(dateObj, lang);
    }

    if (!groupsMap.has(key)) {
      groupsMap.set(key, {
        key,
        title,
        date: dateObj,
        items: [],
      });
    }

    groupsMap.get(key).items.push(item);
  }

  // Ordenar grupos: días más recientes primero, 'no_date' al final
  return Array.from(groupsMap.values()).sort((a, b) => {
    if (a.key === "no_date") return 1;
    if (b.key === "no_date") return -1;
    return b.date.getTime() - a.date.getTime();
  });
}

/**
 * Formatea fecha y hora exactas para el panel de detalles.
 * Ejemplo: "18 de octubre de 2026, 13:57:53"
 */
export function formatExactDateTime(iso, lang = "es") {
  if (!iso) return "No disponible";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "No disponible";
    return d.toLocaleString(lang === "es" ? "es-ES" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "No disponible";
  }
}

/**
 * Formatea bytes en formato legible (KB, MB, GB).
 */
export function formatBytes(bytes) {
  if (bytes == null || isNaN(bytes) || bytes < 0) return "No disponible";
  if (bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}
